/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Shared RN + NativeWind game picker. A modal overlay that lists the
// six game kinds as a grid of icon + label tiles. Presentation-only:
// the host owns what happens on `onPick` (build the `com.ping.game_create`
// envelope via `useGameSessions().createGame(kind)` and `conv.send` it).
//
// Styling follows the project's className-first rule and mirrors the
// scrim + dark-card structure used by `NewChatModal` (Modal portal +
// `StyleSheet.absoluteFill` scrim + tap-to-dismiss). Icons come from
// `lucide-react-native`; web aliases that module to the lucide DOM
// bundle, so the same import works on both surfaces.

import type { LucideIcon } from "lucide-react-native";
import { Car, Coins, Crown, Dices, Hand, Spade, X } from "lucide-react-native";
import React from "react";
import { Modal, Pressable, StyleSheet, Text, View } from "react-native";
import { WebPortal } from "../composer/WebPortal";

const IS_WEB = typeof document !== "undefined" && typeof window !== "undefined";

// GameKind is owned by the appEvent registry agent (packages/ui/src/
// appEvent/types.ts). At the time this file was authored that export
// did not yet exist, so we define the identical string-union locally
// and export it. Once the registry exports `GameKind`, callers can use
// either symbol interchangeably (they are structurally identical) and
// this local definition can be replaced with a re-export.
export type GameKind =
	| "coinflip"
	| "dice"
	| "rps"
	| "chess"
	| "blackjack"
	| "racing";

export interface GamePickerOverlayProps {
	readonly open: boolean;
	readonly onPick: (kind: GameKind) => void;
	readonly onClose: () => void;
}

interface GameTile {
	readonly kind: GameKind;
	readonly label: string;
	// lucide icons share the `LucideIcon` component type ({ size, color,
	// strokeWidth, ... }).
	readonly Icon: LucideIcon;
}

const GAME_TILES: readonly GameTile[] = [
	{ kind: "coinflip", label: "Coin Flip", Icon: Coins },
	{ kind: "dice", label: "Dice Roll", Icon: Dices },
	{ kind: "rps", label: "Rock Paper Scissors", Icon: Hand },
	{ kind: "chess", label: "Chess", Icon: Crown },
	{ kind: "blackjack", label: "Blackjack", Icon: Spade },
	{ kind: "racing", label: "Racing", Icon: Car },
];

export const GamePickerOverlay: React.FC<GamePickerOverlayProps> = ({
	open,
	onPick,
	onClose,
}) => {
	if (!open) return null;

	const content = (
			<Pressable
				accessibilityRole="none"
				onPress={onClose}
				style={StyleSheet.absoluteFill}
				className="
					z-50 flex-1 items-center justify-center px-md
					bg-black/60
				"
			>
				<Pressable
					onPress={() => undefined}
					className="
						w-full max-w-[520px] flex flex-col
						rounded-xl border
						bg-light-surface-primary border-light-border-secondary
						dark:bg-[#1A1D24] dark:border-white/[0.06]
						shadow-2xl
					"
				>
					<View className="flex flex-row items-center justify-between px-lg pt-lg pb-md">
						<Text className="m-0 font-sans font-bold text-headline text-light-text-primary dark:text-white">
							Play a game
						</Text>
						<Pressable
							accessibilityRole="button"
							accessibilityLabel="Close"
							onPress={onClose}
							className="
								w-8 h-8 rounded-lg flex items-center justify-center
								hover:bg-light-surface-tertiary
								dark:hover:bg-white/[0.06]
							"
						>
							<X size={16} strokeWidth={2} color="#475467" />
						</Pressable>
					</View>

					<View className="flex flex-row flex-wrap px-lg pb-lg gap-md">
						{GAME_TILES.map(({ kind, label, Icon }) => (
							<Pressable
								key={kind}
								accessibilityRole="button"
								accessibilityLabel={label}
								onPress={() => onPick(kind)}
								// Two columns: each tile is just under half the row
								// (the `gap-md` between them eats the remainder).
								className="
									basis-[47%] grow
									flex flex-col items-center justify-center gap-sm
									h-28 rounded-xl border
									bg-light-surface-secondary dark:bg-white/[0.04]
									border-light-border-secondary dark:border-white/[0.08]
									hover:bg-light-surface-tertiary
									dark:hover:bg-white/[0.08]
								"
							>
								<Icon size={28} strokeWidth={2} color="#888d96" />
								<Text className="font-sans font-semibold text-body text-light-text-primary dark:text-white text-center">
									{label}
								</Text>
							</Pressable>
						))}
					</View>
				</Pressable>
			</Pressable>
	);

	// On web, render through a persistent body portal instead of RN-Web's
	// Modal — the Modal's two-phase mount gate (a state update + effect
	// before the first paint) is what makes it appear a frame late. The
	// portal paints the overlay the same tick it opens. Native keeps RN
	// Modal.
	if (IS_WEB) {
		return (
			<WebPortal>
				<View style={StyleSheet.absoluteFill}>{content}</View>
			</WebPortal>
		);
	}

	return (
		<Modal visible={open} transparent animationType="none" onRequestClose={onClose}>
			{content}
		</Modal>
	);
};
